import React, { ReactElement, useState, useRef, useEffect, useCallback } from "react";
import {
    OverlayShowDirection,
    OverlayStyleType,
    useGlobalStore,
    usePreferencesStore,
} from "@obsbot/remo-stores";
import { useTranslate } from "@obsbot/remo-i18n";
import classNames from "classnames";
import { DefaultStyleSVG, TabStyleSVG } from "./icons";
import AvatarListCom, { AvatarListRef } from "./components/AvatarList";
import { Radio, Slider } from "antd";
import { constants } from "remo-types";
import { useContainerResizeDone } from "@obsbot/remo-utils";
import "./index.less";

const OverlaySettingPage: React.FC = (): ReactElement => {
    const t = useTranslate();
    const globalStore = useGlobalStore();
    const preferenceStore = usePreferencesStore(globalStore.userInfo?.user_id ?? "")?.(
        state => state,
    );
    const containerRef = useRef<HTMLDivElement | null>(null);

    // 记录当前avatarList的位置
    const [position, setPosition] = useState({
        x: preferenceStore?.overlaySetting.position.x ?? 0,
        y: preferenceStore?.overlaySetting.position.y ?? 0,
    });
    // 记录当前覆盖样式
    const [curStyle, setCurStyle] = useState<OverlayStyleType>(
        preferenceStore?.overlaySetting.overlayStyle ?? OverlayStyleType.Default,
    );
    // 记录当前排列方向
    const [curDirection, setCurDirection] = useState<OverlayShowDirection>(
        preferenceStore?.overlaySetting.showDirection ?? OverlayShowDirection.Vertical,
    );
    // 覆盖画面大小
    const [curSize, setCurSize] = useState<number>(
        preferenceStore?.overlaySetting.overlaySize ?? 50,
    );
    // 覆盖透明度
    const [curOpacity, setCurOpacity] = useState<number>(
        preferenceStore?.overlaySetting.overlayOpacity ?? 50,
    );
    const avatarListRef = useRef<AvatarListRef>(null);
    const [containerReady, setContainerReady] = useContainerResizeDone(containerRef, {
        onReady() {
            setContainerReady(true);
        },
    });
    const [avatarReady, setAvatarReady] = useContainerResizeDone(avatarListRef, {
        onReady() {
            setAvatarReady(true);
        },
    });
    const readyCount = useRef(0);
    // 处理点击保存并退出
    const handleClickSaveAndQuit = (): void => {
        if (!preferenceStore) {
            throw new Error("unexpected preferenceStore is null");
        }
        const newSetting = {
            ...preferenceStore.overlaySetting,
            overlayStyle: curStyle,
            showDirection: curDirection,
            overlaySize: curSize,
            overlayOpacity: curOpacity,
            position: {
                x: position.x,
                y: position.y,
            },
        };
        preferenceStore.updateOverlaySetting(newSetting);
        window.history.back();
        setTimeout(async () => {
            (window as any).electron?.ipcRenderer.send(constants.WindowsName.Main, {
                actions: "set-win-size",
                args: {
                    ...constants.PageSize.Main,
                    autoCenter: true,
                    fullscreen: false,
                },
                browserWindowID: NaN,
            });
        }, 100);
    };
    // 存储timer
    const globalTimer = useRef<NodeJS.Timeout | null>(null);
    const [isChangeSize, setIsChangeSize] = useState(false);
    // 处理改变覆盖大小
    const handleOverlaySizeChanged = (value: number): void => {
        setCurSize(value);
        if (globalTimer.current) {
            clearTimeout(globalTimer.current);
            globalTimer.current = null;
            if (!isChangeSize) {
                setIsChangeSize(true);
            }
        }
        globalTimer.current = setTimeout(() => {
            setIsChangeSize(false);
        }, 200);
    };

    const resetPosition = useCallback(() => {
        if (!preferenceStore) {
            throw new Error("unexpected preferenceStore is null");
        }
        const avatarList = avatarListRef.current;
        const container = containerRef.current;
        if (readyCount.current < 2) {
            readyCount.current += 1;
            return;
        }
        if (
            readyCount.current === 2 &&
            (preferenceStore.overlaySetting.position.x !== undefined ||
                preferenceStore.overlaySetting.position.y !== undefined)
        ) {
            readyCount.current += 1;
            return;
        }
        if (!containerReady || !avatarReady || !avatarList || !container) {
            return;
        }
        const avatarRect = avatarList.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        if (curDirection === OverlayShowDirection.Vertical) {
            setPosition({
                x: 0,
                y: containerRect.height / 2 - avatarRect.height / 2,
            });
        } else {
            setPosition({
                x: containerRect.width / 2 - avatarRect.width / 2,
                y: 0,
            });
        }
    }, [curDirection, containerReady, avatarReady, preferenceStore]);

    useEffect(() => {
        resetPosition();
    }, [resetPosition]);

    const renderOptions = [
        {
            label: t("overlaySettingView.overlayStyle"),
            el: (
                <div className="overlayStyleItems">
                    <div
                        className={classNames("overlayStyleItem", {
                            active: curStyle === OverlayStyleType.Default,
                        })}
                        onClick={() => setCurStyle(OverlayStyleType.Default)}
                    >
                        <div className="content">
                            <DefaultStyleSVG />
                        </div>
                        <div className="content-label">
                            <Radio checked={curStyle === OverlayStyleType.Default}>
                                <div className="text">{t("overlaySettingView.default")}</div>
                            </Radio>
                        </div>
                    </div>
                    <div
                        className={classNames("overlayStyleItem", {
                            active: curStyle === OverlayStyleType.Tab,
                        })}
                        onClick={() => setCurStyle(OverlayStyleType.Tab)}
                    >
                        <div className="content">
                            <TabStyleSVG />
                        </div>
                        <div className="content-label">
                            <Radio checked={curStyle === OverlayStyleType.Tab}>
                                <div className="text">{t("overlaySettingView.tab")}</div>
                            </Radio>
                        </div>
                    </div>
                </div>
            ),
        },
        {
            label: t("overlaySettingView.direction"),
            el: (
                <div className="overlayDirection">
                    <Radio.Group
                        key={curDirection}
                        options={[
                            {
                                value: OverlayShowDirection.Vertical,
                                label: t("overlaySettingView.vertical"),
                                title: "",
                            },
                            {
                                value: OverlayShowDirection.Horizontal,
                                label: t("overlaySettingView.horizontal"),
                                title: "",
                            },
                        ]}
                        value={curDirection}
                        onChange={e => setCurDirection(e.target.value)}
                    />
                </div>
            ),
        },
        {
            label: t("overlaySettingView.coverSize"),
            tips: t("overlaySettingView.coverSizeTip"),
            el: (
                <div className="overlaySize">
                    <Slider
                        className="overlaySettingSlider"
                        max={100}
                        min={10}
                        value={curSize}
                        onChange={handleOverlaySizeChanged}
                    />
                </div>
            ),
        },
        {
            label: t("overlaySettingView.coverOpacity"),
            tips: t("overlaySettingView.coverOpacityTip"),
            el: (
                <div className="overlayOpacity">
                    <Slider
                        className="overlaySettingSlider"
                        max={100}
                        min={10}
                        step={10}
                        tooltip={{
                            formatter(value) {
                                return `${value}%`;
                            },
                        }}
                        value={curOpacity}
                        onChange={setCurOpacity}
                    />
                </div>
            ),
        },
    ];

    return (
        <div ref={containerRef} className="OverlaySettingPage">
            <div className="placeholder mapPlaceHolder">{t("coverGameInfo")}</div>
            <div className="placeholder scoreboardPlaceHolder">{t("coverGameInfo")}</div>
            {/* <div className="placeholder skillsPanel">{t("coverGameInfo")}</div> */}
            <div className="bottomWarnPanel">
                <div className="content">
                    <div className="text">{t("dragWarnText")}</div>
                    <span className="exitBtn no-drag" onClick={handleClickSaveAndQuit}>
                        {t("saveAndQuit")}
                    </span>
                </div>
            </div>
            <AvatarListCom
                ref={avatarListRef}
                containerRef={containerRef}
                curShowDirection={curDirection}
                isChangeSize={isChangeSize}
                opacity={curOpacity}
                overlayStyleType={curStyle}
                position={position}
                setPosition={setPosition}
                size={curSize}
                updateOpacity={setCurOpacity}
                updateSize={setCurSize}
            />
            <div className="overlaySettingPanel">
                <div className="settingContent">
                    <div className="title">{t("overlaySettingView.title")}</div>
                    <div className="setting-options">
                        {renderOptions.map(option => (
                            <div key={option.label} className="setting-item">
                                <div className={"setting-label" + (option.tips ? " tips" : "")}>
                                    <span>{option.label}</span>
                                    {option.tips && <span className="tips">{option.tips}</span>}
                                </div>
                                <div className="setting-input">{option.el}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OverlaySettingPage;
